// TreeView — classic top-down boxes joined by orthogonal lines.
//
// Layout: bottom-up width calculation (each node's slot is the max of
// its own min width and the sum of its children's slot widths) followed
// by a single top-down pass to assign (x, y) coordinates. Children are
// laid out left-to-right inside the parent's slot, the parent is then
// centered above them. This is a simplified Reingold-Tilford that
// doesn't try to share whitespace between sibling subtrees — fine for
// org charts where teams stay visually grouped.
//
// Pure SVG, no d3 dependency. Horizontal scroll handles wide orgs.
import React, { useMemo } from "react";
import { childrenOf, colorFor, nameOf, nodeMatches, withSyntheticRoot } from "./shared";

const NODE_W   = 170;
const NODE_H   = 60;
const H_GAP    = 18;
const LEVEL_H  = 110;
const PAD      = 24;

// Prune the tree to only nodes whose subtree contains a search match.
function prune(node, q) {
  if (!q) return node;
  const kids = childrenOf(node).map((c) => prune(c, q)).filter(Boolean);
  const selfMatches = nodeMatches(node, q) && !node.__synthetic;
  if (kids.length === 0 && !selfMatches) return null;
  return { ...node, reports: kids, children: undefined };
}

// Two-pass layout. Returns the same root augmented with `_x, _y, _w`.
function layout(root) {
  const measure = (n) => {
    const kids = childrenOf(n);
    if (kids.length === 0) {
      n._w = NODE_W + H_GAP;
      return n._w;
    }
    const w = kids.reduce((acc, c) => acc + measure(c), 0);
    n._w = Math.max(NODE_W + H_GAP, w);
    return n._w;
  };
  const place = (n, x, depth) => {
    n._y = PAD + depth * LEVEL_H;
    const kids = childrenOf(n);
    let cursor = x;
    kids.forEach((c) => {
      place(c, cursor, depth + 1);
      cursor += c._w;
    });
    if (kids.length === 0) {
      n._x = x + (n._w - NODE_W) / 2;
    } else {
      // Centre over the first..last child midpoints (not slot midpoint)
      // so the parent sits visually above its actual children.
      const first = kids[0];
      const last  = kids[kids.length - 1];
      const mid   = ((first._x + NODE_W / 2) + (last._x + NODE_W / 2)) / 2;
      n._x = mid - NODE_W / 2;
    }
  };
  measure(root);
  place(root, PAD, 0);
}

function NodeBox({ n, onSelectUser, matched }) {
  const color = colorFor(n.role);
  const dim   = matched === false; // matched is true|false|undefined
  return (
    <g
      transform={`translate(${n._x}, ${n._y})`}
      style={{ cursor: "pointer", opacity: dim ? 0.25 : 1 }}
      onClick={() => onSelectUser?.(n)}
    >
      <rect
        width={NODE_W} height={NODE_H} rx={10} ry={10}
        fill="#0d0d0d"
        stroke={`${color}55`}
        strokeWidth={1}
      />
      <rect width={4} height={NODE_H} rx={2} ry={2} fill={color} />
      <text x={14} y={22} fill="#fff" fontSize={12} fontWeight={600}>
        {truncate(nameOf(n), 22)}
      </text>
      <text x={14} y={38} fill="#888" fontSize={10}>
        {truncate(n.title || n.designation || "—", 26)}
      </text>
      <text x={14} y={52} fill="#666" fontSize={9.5}>
        {n.department ? `${n.department} · ` : ""}
        <tspan fill={color}>{n.role || "staff"}</tspan>
      </text>
    </g>
  );
}

function truncate(s, n) {
  const str = String(s ?? "");
  return str.length > n ? str.slice(0, n - 1) + "…" : str;
}

// Orthogonal connector: drops half-way, jogs across, drops the rest.
function Connector({ from, to }) {
  const x1 = from._x + NODE_W / 2;
  const y1 = from._y + NODE_H;
  const x2 = to._x   + NODE_W / 2;
  const y2 = to._y;
  const my = (y1 + y2) / 2;
  return (
    <path
      d={`M${x1},${y1} L${x1},${my} L${x2},${my} L${x2},${y2}`}
      stroke="#1c1c1c" strokeWidth={1} fill="none"
    />
  );
}

export default function TreeView({ tree, search, onSelectUser }) {
  const root = useMemo(() => {
    const wrapped = withSyntheticRoot(tree);
    const pruned  = prune(wrapped, search);
    if (!pruned) return null;
    // Synthetic root with a single real child: skip the virtual node
    // entirely so the chart starts at the real top of the org.
    const effective = pruned.__synthetic && childrenOf(pruned).length === 1
      ? childrenOf(pruned)[0]
      : pruned;
    layout(effective);
    return effective;
  }, [tree, search]);

  if (!root) {
    return <p className="p-6 text-white/30 text-sm italic">No matches.</p>;
  }

  // Compute SVG bounds by walking the laid-out tree.
  let maxX = 0, maxY = 0;
  const walk = (n) => {
    maxX = Math.max(maxX, n._x + NODE_W);
    maxY = Math.max(maxY, n._y + NODE_H);
    childrenOf(n).forEach(walk);
  };
  walk(root);

  // Collect nodes + edges for a single SVG render pass.
  const nodes = [];
  const edges = [];
  const collect = (n) => {
    nodes.push(n);
    childrenOf(n).forEach((c) => { edges.push([n, c]); collect(c); });
  };
  collect(root);

  return (
    <div className="p-4 overflow-auto max-h-[600px]">
      <svg
        width={maxX + PAD}
        height={maxY + PAD}
        style={{ display: "block", minWidth: "100%" }}
      >
        {edges.map(([p, c], i) => <Connector key={`e${i}`} from={p} to={c} />)}
        {nodes.map((n, i) => (
          <NodeBox key={n.id || `n${i}`} n={n} onSelectUser={onSelectUser} />
        ))}
      </svg>
    </div>
  );
}
